import React, { useState } from "react";
import { AlertTriangle, AlertCircle, Brain, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

const card = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";

function buildSignals(data) {
  if (!data) return [];
  const signals = [];
  const w = data.workforce    || {};
  const p = data.payroll      || {};
  const a = data.attendance   || {};
  const perf = data.performance || {};
  const t = data.tickets      || {};
  const proj = data.projects  || {};

  // Critical (red)
  if (t.open_p1 > 0)
    signals.push({ level: "critical", module: "tickets",     icon: "🔴", title: `${t.open_p1} P1 ticket${t.open_p1 !== 1 ? "s" : ""} open`, detail: "Critical tickets require immediate attention.", link: "/admin/tickets" });
  if (p.exceptions_open > 0)
    signals.push({ level: "critical", module: "payroll",     icon: "🔴", title: `${p.exceptions_open} payroll exception${p.exceptions_open !== 1 ? "s" : ""} unresolved`, detail: "Payroll cannot be approved until these are resolved.", link: "/admin/payroll" });
  if (w.attrition_rate > 15)
    signals.push({ level: "critical", module: "workforce",   icon: "🔴", title: `High attrition: ${w.attrition_rate}% rolling 12 months`, detail: "Attrition is above critical threshold (15%). Immediate retention review required.", link: "/admin/attendance" });

  // Warning (amber)
  if (perf.review_completion < 50 && perf.review_completion != null)
    signals.push({ level: "warning", module: "performance",  icon: "🟡", title: `Only ${perf.review_completion}% of reviews completed`, detail: "Performance cycle is at risk of incomplete submission. Chase managers.", link: "/admin/performance" });
  if (a.today_rate < 75 && a.today_rate != null)
    signals.push({ level: "warning", module: "attendance",   icon: "🟡", title: `Low attendance today: ${a.today_rate}%`, detail: "More than 25% of employees absent. Investigate cause.", link: "/admin/attendance" });
  if (t.sla_breach_rate > 10 && t.sla_breach_rate != null)
    signals.push({ level: "warning", module: "tickets",      icon: "🟡", title: `SLA breach rate: ${t.sla_breach_rate}%`, detail: "Above 10% SLA breach threshold. Review ticket queue capacity.", link: "/admin/tickets" });
  if (proj.overdue_tasks > 20)
    signals.push({ level: "warning", module: "projects",     icon: "🟡", title: `${proj.overdue_tasks} overdue tasks across projects`, detail: "High overdue task count may impact deliverables.", link: "/admin/projects" });
  if (perf.at_risk_count > 5)
    signals.push({ level: "warning", module: "performance",  icon: "🟡", title: `${perf.at_risk_count} employees rated at-risk`, detail: "These employees may need performance improvement plans.", link: "/admin/performance" });
  if (w.pending_leave_approvals > 10)
    signals.push({ level: "warning", module: "leave",        icon: "🟡", title: `${w.pending_leave_approvals} leave requests pending approval`, detail: "Long approval queue may impact employee experience.", link: "/admin/leave" });

  // Info (blue) — no signals? show all-clear
  if (signals.length === 0)
    signals.push({ level: "ok", icon: "✅", title: "No critical signals detected", detail: "All key metrics are within normal ranges." });

  return signals;
}

export default function RiskSignalsPanel({ data, onAskKira }) {
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const signals  = buildSignals(data);
  const criticals = signals.filter(s => s.level === "critical").length;
  const warnings  = signals.filter(s => s.level === "warning").length;

  return (
    <div className={`${card} ${criticals > 0 ? "border-red-500/30" : warnings > 0 ? "border-yellow-500/20" : "border-[#00ff90]/20"}`}>
      <div className="flex items-center justify-between px-5 py-4 cursor-pointer" onClick={() => setExpanded(v => !v)}>
        <div className="flex items-center gap-3">
          <AlertTriangle size={14} className={criticals > 0 ? "text-red-400" : warnings > 0 ? "text-yellow-400" : "text-[#00ff90]/60"} />
          <span className="text-sm font-semibold text-white/80">Risk Signals</span>
          <div className="flex items-center gap-2">
            {criticals > 0 && <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full">{criticals} critical</span>}
            {warnings > 0  && <span className="text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-2 py-0.5 rounded-full">{warnings} warnings</span>}
            {criticals === 0 && warnings === 0 && <span className="text-xs text-[#00ff90]/50 border border-[#00ff90]/15 px-2 py-0.5 rounded-full">All clear</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {signals.some(s => s.level !== "ok") && (
            <button onClick={e => { e.stopPropagation(); onAskKira(data, "Review all current risk signals and provide a prioritised action plan with specific steps I should take in the next 24 hours."); }}
              className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:text-[#00ff90] transition-colors">
              <Brain size={11} /> Action Plan
            </button>
          )}
          {expanded ? <ChevronUp size={14} className="text-white/30" /> : <ChevronDown size={14} className="text-white/30" />}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {signals.map((s, i) => (
            <div key={i}
              className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${
                s.level === "critical" ? "border-red-500/30 bg-red-500/5 cursor-pointer hover:bg-red-500/10" :
                s.level === "warning"  ? "border-yellow-500/20 bg-yellow-500/3 cursor-pointer hover:bg-yellow-500/8" :
                "border-[#00ff90]/15 bg-[#00ff90]/3"
              } transition-colors`}
              onClick={() => s.link && navigate(s.link)}>
              <span className="text-base shrink-0 mt-0.5">{s.icon}</span>
              <div className="min-w-0">
                <p className={`text-sm font-medium ${
                  s.level === "critical" ? "text-red-300" :
                  s.level === "warning"  ? "text-yellow-300" :
                  "text-[#00ff90]"
                }`}>{s.title}</p>
                <p className="text-xs text-white/30 mt-0.5 leading-relaxed">{s.detail}</p>
                {s.link && <p className="text-xs text-white/20 mt-1">→ Click to go to {s.module}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}